import { useMutation } from '@tanstack/react-query';
import { postFormText } from '@/shared/api/api-client';

/**
 * POST /api/Patients/UploadPatientDocument
 *
 * Mirrors Flutter `DocumentDataSourceImpl.uploadDocument`
 * (`document_data_source_impl.dart:92-122`). Multipart wire shape
 * dev-curl verified 2026-05-15 by Andrew.
 *
 * Multipart contract:
 *   - Form field name: `UploadedFile` (verbatim — capital U + F; the
 *     backend looks the part up by that exact key, NOT a lowercased
 *     or hyphenated variant).
 *   - Content-Type: `multipart/form-data; boundary=...` — the browser
 *     sets this automatically when you pass FormData; the api-client
 *     helper strips any manual Content-Type so the boundary doesn't
 *     desync.
 *
 * Response: a signed Azure-blob URL with SAS token, e.g.
 *   `https://scripsqastorage.blob.core.windows.net/patientdocuments/<guid>.<ext>?sv=...&sig=...`
 * Backend writes the file to blob storage and returns the signed read+write
 * URL. The consumer ships this URL on the `patientDocumentFiles[].url`
 * field of the subsequent AddUpdatePatientDocument body.
 *
 * Response shape gotcha: the backend returns a bare string (not JSON-
 * wrapped). We use `postFormText` (multipart variant of `postText`) so
 * the api-client doesn't try to JSON.parse the URL. Defensive strip of
 * surrounding quotes covers the JSON-string-literal case too.
 *
 * Upload progress: Flutter emits `UploadProgressEvent` via the CoreBloc
 * during the dio request. `fetch` doesn't expose upload progress
 * natively; an `XMLHttpRequest`-backed variant is deferred —
 * `UploadProgressEvent` was reclassified MISSING-DEFERRED in PR #258
 * (V1 fire-and-wait is documented as accepted scope). Open a fresh
 * ticket if product wants the progress bar.
 */

export interface UploadDocumentResponse {
  /** Signed blob URL persisted to `patientDocumentFiles[].url`. */
  url: string;
}

export interface UploadDocumentVariables {
  file: File;
}

export function useUploadDocument() {
  return useMutation<UploadDocumentResponse, Error, UploadDocumentVariables>({
    mutationFn: async ({ file }) => {
      const formData = new FormData();
      // Field name MUST be `UploadedFile` (verbatim) — see Flutter
      // `FormData.fromMap({"UploadedFile": MultipartFile.fromBytes(...)})`.
      formData.append('UploadedFile', file, file.name);

      const raw = await postFormText('/api/Patients/UploadPatientDocument', formData);
      // Strip surrounding quotes in case the backend ships the URL as
      // a JSON string literal (`"https://..."`) instead of bare text.
      const url = raw.trim().replace(/^"(.*)"$/, '$1');
      return { url };
    },
  });
}
